/**
 * Iter208.97 — VoiceRecorderButton
 * Bottone microfono per la chat operatori. Registra audio dal browser
 * (MediaRecorder API), lo invia a `POST /api/transcribe` e restituisce
 * il testo trascritto al parent via `onTranscribed(text)`.
 *
 * Stati:
 *   idle          → icona microfono viola, click per iniziare
 *   requesting    → in attesa del permesso microfono
 *   recording     → cerchio rosso pulsante + timer, click per stop
 *   transcribing  → spinner viola
 *   unsupported   → icona bloccata, tooltip
 *   error         → toast + icona ×
 *
 * Il file audio NON viene mai salvato: è solo bufferizzato in memoria
 * lato browser e caricato via multipart/form-data al backend, che a
 * sua volta lo passa a Whisper senza salvarlo.
 */
import React, { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Microphone, Stop, Spinner, MicrophoneSlash, Check, X as XIcon, ArrowClockwise } from "@phosphor-icons/react";

const MAX_SECONDS = 120; // stop automatico dopo 2 minuti

function pickMime() {
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/ogg;codecs=opus",
    "audio/ogg",
    "audio/mp4",
  ];
  for (const m of candidates) {
    if (window.MediaRecorder && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(m)) {
      return m;
    }
  }
  return ""; // il browser sceglierà
}

export function VoiceRecorderButton({ onTranscribed, disabled = false }) {
  const [state, setState] = useState("idle"); // idle | requesting | recording | transcribing | preview | unsupported
  const [seconds, setSeconds] = useState(0);
  const [previewText, setPreviewText] = useState("");
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const autoStopRef = useRef(null);

  // Verifica compatibilità al mount.
  useEffect(() => {
    const supported =
      typeof window !== "undefined" &&
      window.MediaRecorder &&
      navigator.mediaDevices &&
      typeof navigator.mediaDevices.getUserMedia === "function";
    if (!supported) setState("unsupported");
  }, []);

  const cleanup = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (autoStopRef.current) clearTimeout(autoStopRef.current);
    timerRef.current = null;
    autoStopRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    recorderRef.current = null;
    chunksRef.current = [];
    setSeconds(0);
  }, []);

  useEffect(() => () => cleanup(), [cleanup]);

  const sendToBackend = useCallback(async (blob, mime) => {
    setState("transcribing");
    try {
      const fd = new FormData();
      const ext = (mime.split("/")[1] || "webm").split(";")[0];
      fd.append("file", blob, `voice.${ext}`);
      const r = await api.post("/transcribe", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      const text = (r.data?.text || "").trim();
      if (text) {
        setPreviewText(text);
        setState("preview");
      } else {
        toast.error("Nessun parlato riconosciuto.");
        setState("idle");
      }
    } catch (e) {
      const detail = e.response?.data?.detail || "Trascrizione non riuscita. Prova a scrivere manualmente.";
      toast.error(detail);
      setState("idle");
    } finally {
      cleanup();
    }
  }, [cleanup]);

  const confirmPreview = useCallback(() => {
    const t = (previewText || "").trim();
    if (t && typeof onTranscribed === "function") onTranscribed(t);
    setPreviewText("");
    setState("idle");
  }, [previewText, onTranscribed]);

  const discardPreview = useCallback(() => {
    setPreviewText("");
    setState("idle");
  }, []);

  const startRecording = useCallback(async () => {
    if (disabled || state === "unsupported") return;
    setState("requesting");
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (e) {
      setState("idle");
      toast.error(
        e?.name === "NotAllowedError"
          ? "Permesso microfono negato dal browser."
          : "Impossibile accedere al microfono."
      );
      return;
    }
    streamRef.current = stream;
    const mimeType = pickMime();
    let recorder;
    try {
      recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
    } catch (e) {
      cleanup();
      setState("idle");
      toast.error("MediaRecorder non supportato dal browser.");
      return;
    }
    chunksRef.current = [];
    recorder.ondataavailable = (evt) => {
      if (evt.data && evt.data.size > 0) chunksRef.current.push(evt.data);
    };
    recorder.onstop = () => {
      const finalMime = recorder.mimeType || mimeType || "audio/webm";
      const blob = new Blob(chunksRef.current, { type: finalMime });
      if (blob.size < 200) {
        toast.error("Registrazione troppo breve.");
        cleanup();
        setState("idle");
        return;
      }
      sendToBackend(blob, finalMime);
    };
    recorderRef.current = recorder;
    recorder.start();
    setState("recording");
    setSeconds(0);
    timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    autoStopRef.current = setTimeout(() => {
      if (recorderRef.current && recorderRef.current.state === "recording") {
        recorderRef.current.stop();
      }
    }, MAX_SECONDS * 1000);
  }, [disabled, state, sendToBackend, cleanup]);

  const stopRecording = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state === "recording") {
      recorderRef.current.stop();
    }
  }, []);

  const label = (() => {
    if (state === "recording") return `${String(Math.floor(seconds / 60)).padStart(1, "0")}:${String(seconds % 60).padStart(2, "0")}`;
    if (state === "transcribing") return "Trascrizione…";
    if (state === "requesting") return "Attesa…";
    return "";
  })();

  const isBusy = state === "requesting" || state === "recording" || state === "transcribing";

  if (state === "unsupported") {
    return (
      <button
        type="button"
        disabled
        title="Il tuo browser non supporta la registrazione audio"
        className="h-10 px-3 rounded-md border border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed flex items-center gap-1.5"
        data-testid="voice-record-unsupported"
      >
        <MicrophoneSlash size={18} weight="bold" />
      </button>
    );
  }

  if (state === "recording") {
    return (
      <button
        type="button"
        onClick={stopRecording}
        className="h-10 px-3 rounded-md border border-red-200 bg-red-50 text-red-700 flex items-center gap-1.5 hover:bg-red-100"
        title="Ferma la registrazione"
        data-testid="voice-record-stop"
      >
        <span className="relative flex w-2.5 h-2.5">
          <span className="absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75 animate-ping"></span>
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500"></span>
        </span>
        <span className="text-xs font-mono" data-testid="voice-record-timer">{label}</span>
        <Stop size={14} weight="fill" />
      </button>
    );
  }

  if (state === "transcribing" || state === "requesting") {
    return (
      <button
        type="button"
        disabled
        className="h-10 px-3 rounded-md border border-purple-200 bg-purple-50 text-purple-700 flex items-center gap-1.5"
        data-testid="voice-record-busy"
      >
        <Spinner size={16} weight="bold" className="animate-spin" />
        <span className="text-xs">{label}</span>
      </button>
    );
  }

  if (state === "preview") {
    return (
      <div className="relative">
        {/* Balloon di anteprima ancorato sopra il bottone */}
        <div
          className="absolute bottom-full right-0 mb-2 w-[min(320px,calc(100vw-2rem))] rounded-xl border border-purple-200 bg-white shadow-lg p-3 z-40"
          data-testid="voice-preview-balloon"
          role="dialog"
          aria-label="Anteprima trascrizione"
        >
          <div className="flex items-center gap-1.5 mb-2">
            <Microphone size={12} weight="fill" className="text-purple-600" />
            <span className="text-[10px] uppercase tracking-widest text-purple-700 font-semibold">
              Anteprima trascrizione
            </span>
          </div>
          <p
            className="text-sm text-slate-800 leading-relaxed max-h-40 overflow-y-auto whitespace-pre-wrap"
            data-testid="voice-preview-text"
          >
            {previewText}
          </p>
          <div className="flex gap-1.5 mt-3 justify-end">
            <button
              type="button"
              onClick={discardPreview}
              className="h-8 px-2.5 text-xs rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50 flex items-center gap-1"
              title="Scarta e ricomincia"
              data-testid="voice-preview-discard"
            >
              <XIcon size={12} weight="bold" /> Scarta
            </button>
            <button
              type="button"
              onClick={() => { discardPreview(); startRecording(); }}
              className="h-8 px-2.5 text-xs rounded-md border border-purple-200 text-purple-700 hover:bg-purple-50 flex items-center gap-1"
              title="Registra di nuovo"
              data-testid="voice-preview-retry"
            >
              <ArrowClockwise size={12} weight="bold" /> Ripeti
            </button>
            <button
              type="button"
              onClick={confirmPreview}
              className="h-8 px-3 text-xs rounded-md bg-purple-600 text-white hover:bg-purple-700 flex items-center gap-1"
              title="Usa questo testo nella chat"
              data-testid="voice-preview-use"
            >
              <Check size={12} weight="bold" /> Usa
            </button>
          </div>
          {/* Freccetta */}
          <span
            aria-hidden
            className="absolute top-full right-4 w-3 h-3 -mt-1.5 rotate-45 bg-white border-r border-b border-purple-200"
          />
        </div>
        {/* Placeholder del bottone, disabled */}
        <button
          type="button"
          disabled
          className="h-10 w-10 rounded-md border border-purple-300 bg-purple-50 text-purple-500 flex items-center justify-center"
          data-testid="voice-record-preview-anchor"
        >
          <Microphone size={18} weight="bold" />
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={startRecording}
      disabled={disabled || isBusy}
      title="Registra un messaggio vocale (verrà trascritto in testo)"
      className="h-10 w-10 rounded-md border border-slate-200 bg-white text-purple-700 hover:bg-purple-50 flex items-center justify-center"
      data-testid="voice-record-start"
    >
      <Microphone size={18} weight="bold" />
    </button>
  );
}

export default VoiceRecorderButton;
